import React, { useCallback, useState } from 'react';
import { Stack, Text, Button, Overlay } from '@moda/om';
import { PaymentSource } from '../../../constants';
import { titleize } from '../../../lib/titleize';
import { formatPrice } from '../../../lib/formatPrice';
import { useOrderSummary } from '../../../hooks/useOrderSummary';
import { useChargeOrder } from '../../../hooks/useChargeOrder';
import { usePaymentError } from '../../../hooks/useCheckout/usePaymentError';
import { useErrorModal } from '../../../hooks/useErrorModal';
import { useUser } from '../../../hooks/useUser';
import { LoadingBalls } from '../../LoadingBalls';
import { ModalOverlay } from '../../Modal';
import { prettifyCardName } from '../../../lib/prettifyCardName';

import './PayTodayConfirmationModal.scss';

interface Props {
  closeModal: () => void;
  orderNumber: string;
  show: boolean;
}

type PaymentDetail = {
  currency: string;
  price: number | string;
};

type Payment = {
  card: PaymentDetail | null;
  credit: PaymentDetail | null;
  rewards: PaymentDetail | null;
};

export const PayTodayConfirmationModal: React.FC<Props> = ({ orderNumber, closeModal, show }) => {
  const { user } = useUser();
  const { chargeOrder, loading } = useChargeOrder(orderNumber);
  const [isPaid, setIsPaid] = useState(false);
  const { orderSummary } = useOrderSummary(orderNumber);
  const { setPaymentError } = usePaymentError();
  const { showErrorModal } = useErrorModal();

  const [message, setMessage] = useState('');
  const [payment, setPayment] = useState<Payment | null>();

  const handleChargeOrder = useCallback(async () => {
    if (!orderSummary) return;
    const result = await chargeOrder();

    const errorMessage = result && result.data?.chargeOrder.data[0].attributes.errors?.[0];
    const hasError =
      result &&
      result.data?.chargeOrder.data[0].attributes.errors &&
      result.data?.chargeOrder.data[0].attributes.errors.length > 0;

    if (hasError && errorMessage) {
      closeModal();
      setPaymentError(errorMessage ?? null);
      showErrorModal(errorMessage);

      window.newrelic?.addPageAction('secondPaymentFailed', {
        error: errorMessage,
        email: user.email,
        guest: !user.isLoggedIn,
        paymentSource: orderSummary.attributes.paymentMethod.source,
        payToday: orderSummary.attributes.paymentSummary.payToday.price,
        total: orderSummary.attributes.paymentSummary.total.price
      });
    } else if (result && result.data) {
      setIsPaid(true);
      setMessage(result.data.chargeOrder.data[0].attributes.message);
      setPayment(result.data.chargeOrder.data[0].attributes.paymentDetail);
    }
  }, [orderSummary, user, chargeOrder, closeModal, setPaymentError, showErrorModal]);

  if (!orderSummary) return null;

  const {
    paymentMethod,
    paymentSummary: { amountToBeCharged, payToday, rewardsToBeUsed, siteCreditToBeUsed }
  } = orderSummary.attributes;

  return (
    <ModalOverlay className="PayTodayConfirmationModal" onClose={closeModal} show={show}>
      <Stack className="PayTodayConfirmationModal__content" alignItems="center" space={4}>
        {!isPaid && (
          <Text className="PayTodayConfirmationModal__text" family="serif" treatment="h5">
            This will charge {formatPrice(amountToBeCharged.currency, amountToBeCharged.price)} to
            your selected payment method(s) immediately. Do you wish to proceed?
          </Text>
        )}
        {isPaid && (
          <Text className="PayTodayConfirmationModal__text" family="serif" treatment="h5">
            {message}
          </Text>
        )}

        <Stack space={2}>
          <Text treatment="body2">Here is a breakdown of your payment:</Text>
          {!isPaid && (
            <ul>
              {Number(payToday.price) > 0 && (
                <li>
                  <Text className="PayTodayConfirmationModal__text" treatment="body1">
                    {paymentMethod.source === PaymentSource.Adyen
                      ? `${prettifyCardName(paymentMethod.cardType)} ${paymentMethod.lastFour}`
                      : titleize(paymentMethod.source)}
                    : {formatPrice(payToday.currency, payToday.price)}
                  </Text>
                </li>
              )}

              {Number(rewardsToBeUsed.price) > 0 && (
                <li>
                  <Text treatment="body1">
                    M'O Rewards: {formatPrice(rewardsToBeUsed.currency, rewardsToBeUsed.price)}
                  </Text>
                </li>
              )}

              {Number(siteCreditToBeUsed.price) > 0 && (
                <li>
                  <Text treatment="body1">
                    Site Credit:{' '}
                    {formatPrice(siteCreditToBeUsed.currency, siteCreditToBeUsed.price)}
                  </Text>
                </li>
              )}
            </ul>
          )}

          {isPaid && payment && (
            <ul>
              {Number(payment.card?.price) > 0 && (
                <li>
                  <Text className="PayTodayConfirmationModal__text" treatment="body1">
                    {paymentMethod.source === PaymentSource.Adyen
                      ? `${prettifyCardName(paymentMethod.cardType)} ${paymentMethod.lastFour}`
                      : titleize(paymentMethod.source)}
                    : {formatPrice(payment.card?.currency, payment.card?.price)}
                  </Text>
                </li>
              )}
              {Number(payment.credit?.price) > 0 && (
                <li>
                  <Text treatment="body1">
                    Site Credit: {formatPrice(payment.credit?.currency, payment.credit?.price)}
                  </Text>
                </li>
              )}
              {Number(payment.rewards?.price) > 0 && (
                <li>
                  <Text treatment="body1">
                    M'O Rewards: {formatPrice(payment.rewards?.currency, payment.rewards?.price)}
                  </Text>
                </li>
              )}
            </ul>
          )}
        </Stack>

        <Text className="PayTodayConfirmationModal__text" family="serif" treatment="body2">
          *This change will not affect the estimated delivery date of your item(s).
        </Text>

        <Stack space={2}>
          {!isPaid && (
            <>
              <Button onClick={handleChargeOrder}>Yes, pay remaining balance</Button>
              <Button secondary onClick={closeModal}>
                Cancel
              </Button>
            </>
          )}

          {isPaid && <Button onClick={closeModal}>OK</Button>}
        </Stack>

        <Overlay show={loading}>
          <LoadingBalls />
        </Overlay>
      </Stack>
    </ModalOverlay>
  );
};
